import React, { useEffect, useState } from "react";
import axios from "axios";
import { format, getDaysInMonth, subMonths, addMonths, isSunday } from "date-fns";
import * as XLSX from "xlsx";
import { postData1 } from "../../APIs";

const statusColors = {
  Present: "bg-green-500 text-white", // Present
  Absent: "bg-red-500 text-white", // Absent
  Leave: "bg-yellow-500 text-black", // Leave
  Half: "bg-gray-900 text-white", // Half
  "W/o": "bg-blue-500 text-white", // Weekend Off
  Holiday: "bg-blue-900 text-white", // Holiday
  Pending: "bg-gray-500 text-white", // Pending
  Wfh : "bg-pink-300 text-black", // Work From Home
  Off: "bg-gray-300 text-black", // Sunday Off
};

const AttendanceTable = () => {
  const [data, setData] = useState([]);
  const [holiday, setHoliday] = useState([]);
  const [month, setMonth] = useState(new Date());
  const daysInMonth = getDaysInMonth(month);

  useEffect(() => {
    fetchAttendanceData();
  }, [month]);

  const fetchAttendanceData = async () => {
    try {
      var data = {
        from_date: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-01`,
        to_date: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${getDaysInMonth(month)}`,
        is_deleted: false,
      };
      const response = await postData1("api/v1/admin/atten_list/", data, true);
      if (response.status_code === 200) {
        setData(response.data);
        setHoliday(response.holidays);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const formatAttendanceData = () => {
    const attendanceMap = {};
  
    data.forEach((record) => {
      const name = `${record.profile.first_name} ${record.profile.last_name}`;
      if (!attendanceMap[name]) {
        attendanceMap[name] = new Array(daysInMonth).fill("");
      }
  
      const date = new Date(record.date);
      const dayIndex = date.getDate() - 1;
  
      attendanceMap[name][dayIndex] = record.status; 
    });
  
    // Ensure Sundays are marked as "Off" even if no data exists
    for (let day = 0; day < daysInMonth; day++) {
      const currentDate = new Date(month.getFullYear(), month.getMonth(), day + 1);
      if (isSunday(currentDate)) {
        Object.keys(attendanceMap).forEach((name) => {
          attendanceMap[name][day] = "Off"; 
        });
      }
      holiday.forEach((h) => {
        const holidayDate = new Date(h.date);
        const dayIndex = holidayDate.getDate() - 1;
        if (holidayDate.getFullYear() === month.getFullYear() && holidayDate.getMonth() === month.getMonth()) {
          Object.keys(attendanceMap).forEach((name) => {
            attendanceMap[name][dayIndex] = "Holiday"; 
          });
        }
      });
    }
  
    return attendanceMap;
  };

  const exportToExcel = () => {
    const attendanceData = formatAttendanceData();
    const headers = ["Employee Name", ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const rows = Object.entries(attendanceData).map(([name, attendance]) => [name, ...attendance]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
    XLSX.writeFile(workbook, `Attendance_${format(month, "yyyy-MM")}.xlsx`);
  };

  

  const handlePrevMonth = () => {
    setMonth(subMonths(month, 1));
  };

  const handleNextMonth = () => {
    setMonth(addMonths(month, 1));
  };

  const attendanceData = formatAttendanceData();
  const getStatuCode = (status, attendance) => {
    switch (status) {
      case "Present":
        return "P";
      case "Absent":
        return "AB";
      case "Leave":
        return "L";
      case "Half":
        return "H";
      case "W/o":
        return "W/o";
      case "Holiday":
        return "H";
      case "Pending":
        return "A";
      case "Off":
        return "W";
      case "Wfh":
        return "WFH";
      default:
        return "";
    }
  }
  
  return (
    <div className="p-4 font-sans h-screen flex flex-col">
      <h2 className="text-2xl font-semibold mb-2">Attendance Table</h2>
      <div className="attendanceType flex justify-between items-center mb-2">
        <div className="flex gap-4">
          {Object.entries(statusColors).map(([status, color]) => (
            <div key={status} className="flex gap-1 items-center">
              <span className={`w-4 h-4 ${color}`}></span>
              <p className="text-base font-semibold">{status}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between items-center mb-4">
        <button className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600" onClick={handlePrevMonth}>Prev Month</button>
        <span className="text-xl font-bold">{format(month, "MMMM yyyy")}</span>
        <button className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600" onClick={handleNextMonth}>Next Month</button>
      </div>
      <button className="bg-blue-500 text-white px-4 py-2 rounded mb-4 hover:bg-blue-600" onClick={exportToExcel}>Export to Excel</button>
      <div className="overflow-x-auto flex-grow">
        <div className="overflow-y-auto h-[80%] border border-gray-300 rounded-md">
          <table className="w-full border-collapse border border-gray-300">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className="border border-gray-300 px-4 py-2">Employee Name</th>
                {Array.from({ length: daysInMonth }, (_, i) => (
                  <th key={i} className="border border-gray-300 px-4 py-2">{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(attendanceData).map(([name, attendance]) => (
                <tr key={name} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                  <td className="border border-gray-300 px-4 py-2 font-medium">{name}</td>
                  {attendance.map((status, i) => (
                    <td key={i} className={`border border-gray-300 px-1 py-1 text-center ${statusColors[status] || ""}`}>{getStatuCode(status, attendance[i])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTable;
